import { invariant, isDefined, isEmpty, last } from "@banjoanton/utils";
import type { ChessGame } from "../models/chess-game-model";
import type { PerformanceModel } from "../models/stats/performance-model";
import {
    type ChessStats,
    type ChessStatsByType,
    type RatingDataPoint,
    STATS_VERSION,
} from "../models/stats/stats-model";

const earliestFirst = (a: ChessGame, b: ChessGame) =>
    a.endTimeUnix - b.endTimeUnix;

const calculatePerformance = (games: ChessGame[]): PerformanceModel => {
    let wins = 0;
    let losses = 0;
    let draws = 0;

    const sorted = games.toSorted(earliestFirst);

    let combinedAccuracy = 0;
    let accountedAccuracyGames = 0;

    for (let i = 0; i < sorted.length; i++) {
        const game = sorted[i];
        invariant(game, "Game should be defined when calculating performance");

        if (game.playerResult === "win") {
            wins++;
        } else if (game.playerResult === "loss") {
            losses++;
        } else {
            draws++;
        }

        if (game.player.externalAnalysis?.accuracy) {
            combinedAccuracy += game.player.externalAnalysis.accuracy / 100;
            accountedAccuracyGames++;
        }
    }

    const winPercentage = Number((wins / games.length).toFixed(4));
    const averageAccuracy = Number(
        (combinedAccuracy / accountedAccuracyGames).toFixed(4)
    );

    const model: PerformanceModel = {
        wins,
        losses,
        draws,
        winPercentage,
        accuracy: {
            totalGames: games.length,
            gamesAccountedFor: accountedAccuracyGames,
            totalAccuracy: averageAccuracy,
        },
    };

    return model;
};

// eslint-disable-next-line complexity
const getStats = (sortedGames: ChessGame[]) => {
    const gamesAsBlack: ChessGame[] = [];
    const gamesAsWhite: ChessGame[] = [];
    const ratedGames: ChessGame[] = [];
    const gamesByType: Record<string, ChessGame[]> = {};
    const unratedGames = [];
    let lowestRatedGame: ChessGame | undefined = undefined;
    let highestRatedGame: ChessGame | undefined = undefined;

    for (let i = 0; i < sortedGames.length; i++) {
        const game = sortedGames[i];
        invariant(game, "Game should be defined when calculating stats");

        if (game.playerColor === "w") {
            gamesAsWhite.push(game);
        } else {
            gamesAsBlack.push(game);
        }

        if (game.rated) {
            ratedGames.push(game);
        } else {
            unratedGames.push(game);
        }

        const type = game.time.class; // TODO: add validation for this type
        if (!gamesByType[type]) {
            gamesByType[type] = [];
        }
        gamesByType[type]?.push(game);

        const lowestRatedGamePlayer =
            lowestRatedGame?.username === lowestRatedGame?.white.username
                ? lowestRatedGame?.white
                : lowestRatedGame?.black;

        if (!lowestRatedGamePlayer) {
            lowestRatedGame = game;
        } else if (game.player.rating < lowestRatedGamePlayer.rating) {
            lowestRatedGame = game;
        }

        const highestRatedGamePlayer =
            highestRatedGame?.username === highestRatedGame?.white.username
                ? highestRatedGame?.white
                : highestRatedGame?.black;

        if (!highestRatedGamePlayer) {
            highestRatedGame = game;
        } else if (game.player.rating > highestRatedGamePlayer.rating) {
            highestRatedGame = game;
        }
    }

    const collectionId = sortedGames.find((g) => g.collectionId)?.collectionId;

    invariant(
        collectionId,
        "Cannot calculate stats for games without a collectionId"
    );

    invariant(lowestRatedGame, "There should be at least one game");
    invariant(highestRatedGame, "There should be at least one game");

    const lowestRatedGamePlayer =
        lowestRatedGame.username === lowestRatedGame.white.username
            ? lowestRatedGame.white
            : lowestRatedGame.black;

    const highestRatedGamePlayer =
        highestRatedGame.username === highestRatedGame.white.username
            ? highestRatedGame.white
            : highestRatedGame.black;

    return {
        ratedGames,
        unratedGames,
        gamesAsBlack,
        gamesAsWhite,
        lowestRatedGame,
        lowestRatedGamePlayer,
        highestRatedGame,
        highestRatedGamePlayer,
        gamesByType,
    };
};

const calculateStats = (
    games: ChessGame[],
    collectionId: string
): ChessStats => {
    const sorted = games.toSorted(earliestFirst);

    const {
        ratedGames,
        gamesAsBlack,
        gamesAsWhite,
        highestRatedGame,
        highestRatedGamePlayer,
        lowestRatedGame,
        lowestRatedGamePlayer,
        unratedGames,
        gamesByType,
    } = getStats(sorted);

    const statsByType: ChessStatsByType[] = Object.entries(gamesByType)
        .map(([type, games]) => {
            if (isEmpty(games)) {
                return undefined;
            }
            const sortedByType = games.toSorted(earliestFirst);
            const {
                ratedGames: ratedGamesByType,
                gamesAsBlack: gamesAsBlackByType,
                gamesAsWhite: gamesAsWhiteByType,
                highestRatedGame: highestRatedGameByType,
                highestRatedGamePlayer: highestRatedGamePlayerByType,
                lowestRatedGame: lowestRatedGameByType,
                lowestRatedGamePlayer: lowestRatedGamePlayerByType,
            } = getStats(sortedByType);

            const currentRatingGame = last(games);
            invariant(
                currentRatingGame,
                "There should be at least one game of this type"
            );

            const ratingHistory: RatingDataPoint[] = sortedByType.map(
                (game) => ({
                    timestamp: game.endTimeUnix,
                    rating: game.player.rating,
                })
            );

            const stats: ChessStatsByType = {
                type,
                currentRating: currentRatingGame.player.rating,
                highestRating: highestRatedGamePlayerByType.rating,
                highestRatingGameId: highestRatedGameByType.id,
                lowestRating: lowestRatedGamePlayerByType.rating,
                lowestRatingGameId: lowestRatedGameByType.id,
                lastUpdated: new Date().getTime(),
                performanceByColor: {
                    black: {
                        total: calculatePerformance(gamesAsBlackByType),
                        rated: calculatePerformance(
                            gamesAsBlackByType.filter((g) => g.rated)
                        ),
                    },
                    white: {
                        total: calculatePerformance(gamesAsWhiteByType),
                        rated: calculatePerformance(
                            gamesAsWhiteByType.filter((g) => g.rated)
                        ),
                    },
                },
                totalPerformance: calculatePerformance(sortedByType),
                ratedPerformance: calculatePerformance(ratedGamesByType),
                totalGames: sortedByType.length,
                ratingHistory,
            };

            return stats;
        })
        .filter(isDefined);

    const stats: ChessStats = {
        collectionId,
        version: STATS_VERSION,
        ratedPerformance: calculatePerformance(ratedGames),
        totalPerformance: calculatePerformance(sorted),
        performanceByColor: {
            black: {
                total: calculatePerformance(gamesAsBlack),
                rated: calculatePerformance(
                    gamesAsBlack.filter((g) => g.rated)
                ),
            },
            white: {
                total: calculatePerformance(gamesAsWhite),
                rated: calculatePerformance(
                    gamesAsWhite.filter((g) => g.rated)
                ),
            },
        },
        totalGames: sorted.length,
        totalRatedGames: ratedGames.length,
        totalUnratedGames: unratedGames.length,
        lowestRating: lowestRatedGamePlayer.rating,
        lowestRatingGameId: lowestRatedGame.id,
        highestRating: highestRatedGamePlayer.rating,
        highestRatingGameId: highestRatedGame.id,
        statsByType,
    };

    return stats;
};

export const StatsService = { calculateStats };
